import { ref, watch } from "vue";

export enum ResponsiveSizes {
  xs = 300,
  sm = 640,
  md = 768,
  lg = 1024,
  xl = 1280,
  xxl = 1536,
}

export type BreakpointType = "xs" | "sm" | "md" | "lg" | "xl" | "xxl";
export type VisibleType = "block" | "hidden";

export type BreakpointsType = {
  [breakpoint in BreakpointType]: ResponsiveSizes;
};
export type VisibleSizesType = {
  [size in ResponsiveSizes]?: VisibleType;
};

const screenWidth = ref(window.innerWidth);
window.onresize = () => {
  screenWidth.value = window.innerWidth;
};

export const useDsResponsive = () => {
  const breakpoints: BreakpointsType = {
    xs: ResponsiveSizes.xs,
    sm: ResponsiveSizes.sm,
    md: ResponsiveSizes.md,
    lg: ResponsiveSizes.lg,
    xl: ResponsiveSizes.xl,
    xxl: ResponsiveSizes.xxl,
  };
  const types: VisibleType[] = ["block", "hidden"];

  const grid = (args: string): boolean => {
    const visible = ref(true);

    let visibleDefault: VisibleType = "block";
    const visibleSizes: VisibleSizesType = {};

    const setVisibility = (): void => {
      let visibilityType = visibleDefault;

      Object.keys(visibleSizes).forEach((key) => {
        const size = Number(key);
        const visibility = Reflect.get(visibleSizes, key) as VisibleType;

        if (screenWidth.value >= size) {
          visibilityType = visibility;
        }
      });
      visible.value = visibilityType === "block";
    };

    if (args) {
      const filters = args.split(" ");

      // We iterate over the args
      filters.forEach((val) => {
        const [breakpoint, type] = val.split(":");

        // If the args doesn't have a breakpoint we should use the 'breakpoint' as 'type'
        if (!type && types.includes(breakpoint as VisibleType)) {
          visibleDefault = breakpoint as VisibleType;
        }

        // If the breakpoint exist in the breakpoints object and the visibility type exists
        if (
          breakpoints[breakpoint as BreakpointType] &&
          types.includes(type as VisibleType)
        ) {
          // We create an object with the sizes and their visibility
          Reflect.set(
            visibleSizes,
            breakpoints[breakpoint as BreakpointType],
            type as VisibleType
          );
        }
      });
    }

    setVisibility();

    watch(screenWidth, () => {
      setVisibility();
    });

    return visible.value;
  };

  return {
    grid,
  };
};
